import React from 'react';
import { createMemoStyles, removeUndefinedProperties, createSpacingStyles, clsx, safeRest } from '@wds/utils';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign$1 = function () {
  __assign$1 = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign$1.apply(this, arguments);
};
function __rest$1(s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function () {
  __assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
function __rest(s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}
var useStyles$1 = createMemoStyles(function (_a) {
  var spacing = _a.spacing,
    border = _a.border;
  return {
    wrenchBox: function (_a) {
      var _b, _c;
      var display = _a.display,
        flexProperties = _a.flexProperties,
        gridProperties = _a.gridProperties,
        borderColor = _a.borderColor;
      var gap = gridProperties.gap,
        gridStyleProperties = __rest(gridProperties, ["gap"]);
      var _d = flexProperties.flexItemGrow,
        flexItemGrow = _d === void 0 ? undefined : _d,
        _e = flexProperties.flexWrap,
        flexWrap = _e === void 0 ? undefined : _e,
        flexStyleProperties = __rest(flexProperties, ["flexItemGrow", "flexWrap"]);
      return removeUndefinedProperties(__assign(__assign(__assign({}, gridStyleProperties), flexStyleProperties), {
        justifyContent: (_b = gridStyleProperties.justifyContent) !== null && _b !== void 0 ? _b : flexStyleProperties.justifyContent,
        alignItems: (_c = gridStyleProperties.alignItems) !== null && _c !== void 0 ? _c : flexStyleProperties.alignItems,
        flexGrow: flexItemGrow && 1,
        flexWrap: flexWrap && 'wrap',
        display: display !== null && display !== void 0 ? display : 'block',
        gap: gap ? spacing[gap] : null,
        border: borderColor && border[borderColor]
      }));
    }
  };
});

/**
 * [IMPORTANT]
 * We have to call createSpacingStyles at the component level
 * Otherwise, the styles will be overridden by the global styles
 * This happens because JSS loads the styles in sequence
 */
var useSpacingStyles = createSpacingStyles();
var Box = function (props) {
  var containerRef = props.containerRef,
    borderColor = props.borderColor,
    className = props.className,
    children = props.children,
    _a = props.element,
    element = _a === void 0 ? 'div' : _a,
    display = props.display,
    alignItems = props.alignItems,
    justifyContent = props.justifyContent,
    flexDirection = props.flexDirection,
    flexItemGrow = props.flexItemGrow,
    flexJustify = props.flexJustify,
    flexAlign = props.flexAlign,
    flexWrap = props.flexWrap,
    gridTemplateColumns = props.gridTemplateColumns,
    gridTemplateRows = props.gridTemplateRows,
    gridAutoFlow = props.gridAutoFlow,
    placeItems = props.placeItems,
    gap = props.gap,
    m = props.m,
    mx = props.mx,
    my = props.my,
    mt = props.mt,
    mb = props.mb,
    ml = props.ml,
    mr = props.mr,
    p = props.p,
    px = props.px,
    py = props.py,
    pt = props.pt,
    pb = props.pb,
    pl = props.pl,
    pr = props.pr,
    rest = __rest(props, ["containerRef", "borderColor", "className", "children", "element", "display", "alignItems", "justifyContent", "flexDirection", "flexItemGrow", "flexJustify", "flexAlign", "flexWrap", "gridTemplateColumns", "gridTemplateRows", "gridAutoFlow", "placeItems", "gap", "m", "mx", "my", "mt", "mb", "ml", "mr", "p", "px", "py", "pt", "pb", "pl", "pr"]);
  var flexProperties = {
    alignItems: alignItems !== null && alignItems !== void 0 ? alignItems : flexAlign,
    justifyContent: justifyContent !== null && justifyContent !== void 0 ? justifyContent : flexJustify,
    flexDirection: flexDirection,
    flexItemGrow: flexItemGrow,
    flexWrap: flexWrap
  };
  var gridProperties = {
    justifyContent: justifyContent,
    alignItems: alignItems,
    gridTemplateColumns: gridTemplateColumns,
    gridTemplateRows: gridTemplateRows,
    gridAutoFlow: gridAutoFlow,
    placeItems: placeItems,
    gap: gap
  };
  var wrenchSpacing = useSpacingStyles({
    m: m,
    mx: mx,
    my: my,
    mt: mt,
    mb: mb,
    ml: ml,
    mr: mr,
    p: p,
    px: px,
    py: py,
    pt: pt,
    pb: pb,
    pl: pl,
    pr: pr
  }).wrenchSpacing;
  var wrenchBox = useStyles$1({
    flexProperties: flexProperties,
    gridProperties: gridProperties,
    display: display,
    borderColor: borderColor
  }).wrenchBox;
  // TODO: remove this type coercion when upgrading to React v18
  var Element = element;
  var classNames = clsx(className, wrenchBox, wrenchSpacing);
  return React.createElement(Element, __assign({
    className: classNames,
    ref: containerRef
  }, rest), children);
};
Box.defaultProps = {
  element: 'div',
  containerRef: undefined,
  display: undefined,
  children: null,
  className: undefined
};
var useStyles = createMemoStyles(function (theme) {
  var type = theme.type,
    spacing = theme.spacing,
    palette = theme.palette,
    radius = theme.radius;
  var badgeStyles = {
    wrenchBadge: function (_a) {
      var color = _a.color;
      return {
        display: 'inline-flex',
        alignItems: 'center',
        position: 'relative',
        backgroundColor: palette.background[color].medium,
        color: palette.text.dark,
        padding: [0, spacing[2]],
        height: spacing[5],
        borderRadius: radius.small,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        verticalAlign: 'middle',
        maxWidth: '100%'
      };
    },
    wrenchBadgeContent: function () {
      return {
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        fontWeight: type.fontWeight.medium,
        fontFamily: type.fontFamily.base,
        fontSize: type.fontSize.bodySm
      };
    },
    wrenchBadgeText: function () {
      return {
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      };
    }
  };
  return badgeStyles;
});
var Badge = function (_a) {
  var children = _a.children,
    _b = _a.color,
    color = _b === void 0 ? 'muted' : _b,
    _c = _a.icon,
    icon = _c === void 0 ? undefined : _c,
    rest = __rest$1(_a, ["children", "color", "icon"]);
  var _d = useStyles({
      color: color
    }),
    wrenchBadge = _d.wrenchBadge,
    wrenchBadgeContent = _d.wrenchBadgeContent,
    wrenchBadgeText = _d.wrenchBadgeText;
  return React.createElement("span", __assign$1({
    className: wrenchBadge
  }, safeRest(rest)), React.createElement("span", {
    className: wrenchBadgeContent
  }, icon && React.createElement(Box, {
    element: "span",
    mr: 2,
    display: 'flex',
    alignItems: 'center'
  }, icon), React.createElement("span", {
    className: wrenchBadgeText
  }, children)));
};
var BADGE_COLORS = ['primary', 'success', 'warning', 'error', 'info', 'upgrade', 'muted'];
export { BADGE_COLORS, Badge };